import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Header } from '../components/layout/Header';
import { Footer } from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Clock, FileText, Search, TrendingUp, Truck, Users, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { SEO } from '../components/SEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const services = [
  {
    id: "trade-consultation",
    title: "Trade Consultation",
    price: 250,
    unit: "per hour",
    description: "One-on-one expert advisory session on import/export strategy, market entry, and trade optimisation.",
    icon: Users,
    image: "https://static.prod-images.emergentagent.com/jobs/ab16ff49-84df-46ce-8424-b54bab757cbb/images/61a58d1f0c82713885cddb29623d849db202b88114bedcf6ff744e0e318e0635.png",
    features: [
      "60-minute video consultation",
      "Personalised trade advice",
      "Market entry strategy",
      "Follow-up summary report"
    ],
    popular: false
  },
  {
    id: "market-research",
    title: "Market Research Report",
    price: 999,
    unit: "per report",
    description: "Comprehensive custom report analyzing specific markets, competitors, and opportunities for your products.",
    icon: TrendingUp,
    image: "https://static.prod-images.emergentagent.com/jobs/ab16ff49-84df-46ce-8424-b54bab757cbb/images/bdb850d9ba0e82a0db02f1a1ae59e94f518e155d6aa7001cd5008eadc7851b1e.png",
    features: [
      "In-depth market analysis",
      "Competitor landscape",
      "Entry barriers & opportunities",
      "Pricing recommendations",
      "30-day delivery"
    ],
    popular: true
  },
  {
    id: "supplier-verification",
    title: "Supplier Verification",
    price: 349,
    unit: "per supplier",
    description: "Thorough background check and verification of potential suppliers to minimise trade risks.",
    icon: Search,
    image: "https://static.prod-images.emergentagent.com/jobs/ab16ff49-84df-46ce-8424-b54bab757cbb/images/11e1eb4028e528f8a4b44932abb089ae3a3d8afe205d9972f4aa16e1d061b898.png",
    features: [
      "Business registration check",
      "Financial stability review",
      "Reference verification",
      "Risk assessment report"
    ],
    popular: false
  },
  {
    id: "document-review",
    title: "Document Review",
    price: 149,
    unit: "per review",
    description: "Expert review of your trade documents, contracts, and compliance paperwork.",
    icon: FileText,
    image: "https://static.prod-images.emergentagent.com/jobs/ab16ff49-84df-46ce-8424-b54bab757cbb/images/6fa64b359f9dd8a8906ababfc13e95e482f3a6b2978bd78eabad0f64dd706d22.png",
    features: [
      "Contract review",
      "Compliance check",
      "Risk identification",
      "Improvement recommendations"
    ],
    popular: false
  },
  {
    id: "feasibility-study",
    title: "Trade Feasibility Study",
    price: 1500,
    unit: "per study",
    description: "Full analysis for new market entry including viability, costs, regulations, and go-to-market strategy.",
    icon: TrendingUp,
    image: "https://static.prod-images.emergentagent.com/jobs/ab16ff49-84df-46ce-8424-b54bab757cbb/images/17facca292632b68db079a88655b11fc8f0dad63abcdbc7f81d71e7cad52ab53.png",
    features: [
      "Market viability analysis",
      "Cost-benefit projection",
      "Regulatory requirements",
      "Risk assessment",
      "Go-to-market roadmap",
      "45-day delivery"
    ],
    popular: false
  },
  {
    id: "express-sourcing",
    title: "Express Sourcing",
    price: 499,
    unit: "per request",
    description: "Priority product sourcing service with faster turnaround and dedicated account management.",
    icon: Truck,
    image: "https://static.prod-images.emergentagent.com/jobs/ab16ff49-84df-46ce-8424-b54bab757cbb/images/7ed7728dce1027c865780736ec679ef93a3c19ab7a3b5fec1fbbb3c7e0cfbaac.png",
    features: [
      "Dedicated sourcing team",
      "Priority processing",
      "Multiple supplier options",
      "Quality verification",
      "14-day turnaround"
    ],
    popular: false
  }
];

const faqs = [
  {
    question: "How do I pay for services?",
    answer: "We accept all major credit and debit cards through our secure Stripe payment gateway. Your payment information is encrypted and never stored on our servers."
  },
  {
    question: "Can I get a refund?",
    answer: "Yes, we offer a full refund if you cancel within 24 hours of purchase, provided work has not yet commenced. For consultations, rescheduling is available up to 48 hours before your session."
  },
  {
    question: "How long does delivery take?",
    answer: "Delivery times vary by service. Document reviews take 3-5 business days, supplier verifications 5-7 days, market research reports 30 days, and feasibility studies 45 days."
  },
  {
    question: "Do you offer custom packages?",
    answer: "Yes! For larger projects or ongoing needs, we offer customised packages. Contact us through the enquiry form to discuss your specific requirements."
  }
];

export default function PricingPage() {
  const [searchParams] = useSearchParams();
  const [loadingService, setLoadingService] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [isPolling, setIsPolling] = useState(false);

  // Check for return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');

    if (sessionId && status === 'success') {
      setIsPolling(true);
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled');
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setIsPolling(false);
      setPaymentStatus({ status: 'timeout', message: 'Payment verification timed out. Please check your email for confirmation.' });
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');

      const data = await response.json();

      if (data.payment_status === 'paid') {
        setIsPolling(false);
        setPaymentStatus({
          status: 'success',
          message: `Payment successful for ${data.service_name}!`,
          amount: data.amount_total,
          currency: data.currency
        });
        toast.success('Payment successful! Thank you for your purchase.');
      } else if (data.status === 'expired') {
        setIsPolling(false);
        setPaymentStatus({ status: 'expired', message: 'Payment session expired. Please try again.' });
      } else {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
      } else {
        setIsPolling(false);
        setPaymentStatus({ status: 'error', message: 'Error verifying payment. Please contact support.' });
      }
    }
  };

  const handleBookService = async (serviceId) => {
    setLoadingService(serviceId);

    try {
      const originUrl = window.location.origin;

      const response = await fetch(`${API_URL}/api/checkout/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: serviceId,
          origin_url: originUrl
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to create checkout session');
      }

      const data = await response.json();

      // Redirect to Stripe checkout
      if (data.url) {
        window.location.href = data.url;
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.message || 'Failed to initiate payment. Please try again.');
    } finally {
      setLoadingService(null);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Pricing"
        path="/pricing"
        description="Professional trade consulting services and pricing. Expert advisory, market research, supplier verification, document review, and more from Naviton Global."
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-navy-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-block px-4 py-1.5 rounded-full bg-teal-500/20 text-teal-400 text-sm font-medium mb-6">
                Premium Services
              </span>
              <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
                Service <span className="text-teal-400">Pricing</span>
              </h1>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Expert trade services tailored to your business needs. 
                Choose from our range of professional services to accelerate your international trade journey.
              </p>
            </div>
          </div>
        </section>

        {/* Payment Status Banner */}
        {(paymentStatus || isPolling) && (
          <div className={`py-4 ${
            paymentStatus?.status === 'success' ? 'bg-green-50 border-b border-green-200' :
            paymentStatus?.status === 'error' || paymentStatus?.status === 'expired' ? 'bg-red-50 border-b border-red-200' :
            'bg-blue-50 border-b border-blue-200'
          }`}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex items-center justify-center gap-3">
                {isPolling ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
                    <span className="text-blue-700 font-medium">Verifying your payment...</span>
                  </>
                ) : paymentStatus?.status === 'success' ? (
                  <>
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <span className="text-green-700 font-medium">{paymentStatus.message}</span>
                    <span className="text-green-600">
                      (${paymentStatus.amount?.toFixed(2)} {paymentStatus.currency?.toUpperCase()})
                    </span>
                  </>
                ) : (
                  <>
                    <XCircle className="h-5 w-5 text-red-600" />
                    <span className="text-red-700 font-medium">{paymentStatus?.message}</span>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Pricing Cards */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service) => {
                const IconComponent = service.icon;
                return (
                  <Card
                    key={service.id}
                    className={`relative overflow-hidden bg-white border-2 transition-all duration-300 hover:shadow-xl ${
                      service.popular ? 'border-teal-500' : 'border-slate-200 hover:border-teal-300'
                    }`}
                    data-testid={`service-card-${service.id}`}
                  >
                    {service.popular && (
                      <Badge className="absolute top-4 right-4 z-10 bg-teal-500 text-white">
                        Most Popular
                      </Badge>
                    )}

                    {/* Service Image */}
                    <div className="h-40 overflow-hidden">
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                      />
                    </div>

                    <div className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className={`p-3 rounded-xl ${service.popular ? 'bg-teal-100' : 'bg-slate-100'}`}>
                          <IconComponent className={`h-6 w-6 ${service.popular ? 'text-teal-600' : 'text-slate-600'}`} />
                        </div>
                        <h3 className="text-xl font-bold text-navy-900">{service.title}</h3>
                      </div>

                      <div className="mb-4">
                        <span className="text-4xl font-bold text-navy-900">${service.price}</span>
                        <span className="text-slate-500 ml-2">{service.unit}</span>
                      </div>

                      <p className="text-slate-600 mb-6 min-h-[60px]">
                        {service.description}
                      </p>

                      <ul className="space-y-3 mb-6">
                        {service.features.map((feature, index) => (
                          <li key={index} className="flex items-start gap-2">
                            <Check className="h-5 w-5 text-teal-500 flex-shrink-0 mt-0.5" />
                            <span className="text-slate-600 text-sm">{feature}</span>
                          </li>
                        ))}
                      </ul>

                      <Button
                        onClick={() => handleBookService(service.id)}
                        disabled={loadingService === service.id}
                        data-testid={`book-btn-${service.id}`}
                        className={`w-full py-3 rounded-xl ${
                          service.popular
                            ? 'bg-teal-600 hover:bg-teal-700 text-white'
                            : 'bg-navy-900 hover:bg-navy-800 text-white'
                        }`}
                      >
                        {loadingService === service.id ? (
                          <span className="flex items-center gap-2">
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Processing...
                          </span>
                        ) : (
                          'Book Now'
                        )}
                      </Button>
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Custom Quote CTA */}
        <section className="py-16 bg-navy-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Need a Custom Solution?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              For larger projects, ongoing partnerships, or unique requirements, 
              we offer tailored packages to meet your specific needs.
            </p>
            <a href="/contact">
              <Button className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-4 rounded-xl text-lg">
                Request Custom Quote
              </Button>
            </a>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-navy-900 mb-4">
                Frequently Asked Questions
              </h2>
              <p className="text-lg text-slate-600">
                Common questions about our premium services
              </p>
            </div>

            <div className="space-y-6">
              {faqs.map((faq, index) => (
                <div
                  key={index}
                  className="bg-slate-50 rounded-xl p-6 border border-slate-200"
                  data-testid={`faq-${index}`}
                >
                  <h3 className="text-lg font-semibold text-navy-900 mb-2">
                    {faq.question}
                  </h3>
                  <p className="text-slate-600">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust Badges */}
        <section className="py-12 bg-slate-50 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center items-center gap-8 text-slate-500">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                <span className="text-sm">Fast Turnaround</span>
              </div>
              <div className="flex items-center gap-2">
                <Check className="h-5 w-5" />
                <span className="text-sm">Secure Payments via Stripe</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                <span className="text-sm">Expert Consultants</span>
              </div>
              <div className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                <span className="text-sm">Professional Reports</span>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
